import { createContext, useContext, useState, useEffect } from 'react';

const CartContext = createContext(null);

export function useCart() {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within CartContextProvider');
    }
    return context;
}

export function CartContextProvider({ children }) {
    const [itemSlugs, setItemSlugsState] = useState([]);
    const [numCartItems, setNumCartItemsState] = useState(0);
    const [cartIsSet, setCartIsSetState] = useState(false);
    const [customerProfile, setCustomerProfileState] = useState(null);
    const [deliveryDetails, setDeliveryDetailsState] = useState(null);
    const [payment, setPaymentState] = useState(null);
    const [hasCheckedOut, setHasCheckedOutState] = useState(null);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        try {
            const itemSlugs = JSON.parse(sessionStorage.getItem('itemSlugs') || '[]');
            const numItems = parseInt(sessionStorage.getItem('numItems') || '0', 10);
            const cartId = sessionStorage.getItem('cartId');
            const customerProfile = JSON.parse(sessionStorage.getItem('customerProfile') || 'null');
            const deliveryDetails = JSON.parse(sessionStorage.getItem('deliveryDetails') || 'null');
            const payment = JSON.parse(sessionStorage.getItem('payment') || 'null');
            const hasCheckedOut = sessionStorage.getItem('hasCheckedOut');

            setItemSlugsState(itemSlugs);
            setNumCartItemsState(numItems);
            setCartIsSetState(!!cartId);
            setCustomerProfileState(customerProfile);
            setDeliveryDetailsState(deliveryDetails);
            setPaymentState(payment);
            setHasCheckedOutState(hasCheckedOut);
        } catch (e) {
            console.warn('Failed to restore cart from sessionStorage', e);
        }
    }, []);

    const setItemSlugs = (slugs) => {
        setItemSlugsState(slugs);
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('itemSlugs', JSON.stringify(slugs));
        }
    };

    const setNumItems = (num) => {
        const numInt = parseInt(num, 10) || 0;
        setNumCartItemsState(numInt);
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('numItems', String(numInt));
        }
    };

    const setCart = (cart) => {
        setCartIsSetState(true);
        if (typeof window !== 'undefined' && cart?.cart_id) {
            sessionStorage.setItem('cartId', cart.cart_id);
        }
    };

    const setCustomerProfile = (data) => {
        setCustomerProfileState(data);
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('customerProfile', JSON.stringify(data));
        }
    };

    const setDeliveryDetails = (data) => {
        setDeliveryDetailsState(data);
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('deliveryDetails', JSON.stringify(data));
        }
    };

    const setPayment = (data) => {
        setPaymentState(data);
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('payment', JSON.stringify(data));
        }
    };

    const setHasCheckedOut = (data) => {
        setHasCheckedOutState(data);
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('hasCheckedOut', data);
        }
    };

    const inCart = (slug) => {
        return itemSlugs.some(item => item.slug === slug);
    };

    const getCartId = () => {
        return typeof window !== 'undefined' ? sessionStorage.getItem('cartId') : null;
    };

    const resetCart = () => {
        setItemSlugs([]);
        setNumItems(0);
        setCartIsSetState(false);
        setCustomerProfile(null);
        setDeliveryDetails(null);
        setPayment(null);
        setHasCheckedOut(null);

        if (typeof window !== 'undefined') {
            sessionStorage.removeItem('cartId');
            sessionStorage.removeItem('itemSlugs');
            sessionStorage.removeItem('numItems');
            sessionStorage.removeItem('customerProfile');
            sessionStorage.removeItem('deliveryDetails');
            sessionStorage.removeItem('payment');
            sessionStorage.removeItem('hasCheckedOut');
        }
    };

    return (
        <CartContext.Provider
            value={{
                itemSlugs,
                numCartItems,
                cartIsSet,
                setItemSlugs,
                setNumItems,
                setCart,
                inCart,
                getCartId,
                resetCart,
                customerProfile,
                setCustomerProfile,
                hasCheckedOut,
                setHasCheckedOut,
                deliveryDetails,
                setDeliveryDetails,
                payment,
                setPayment,
            }}
        >
            {children}
        </CartContext.Provider>
    );
}

export default CartContext;